import { useEffect, useRef } from "react";

/**
 * useOverlayDismiss — Escape-to-close and the page scroll lock, for a STACK of
 * overlays rather than a single one.
 *
 * <p>🔴 <b>Both behaviours were per-instance and both broke when two overlays
 * were open at once.</b> `Modal` and `ResourceDrawer` each ran the same effect:
 * add a document-level `keydown` listener, set `body.overflow = "hidden"`, and
 * undo both on unmount. Independently correct; together, wrong in two ways.</p>
 *
 * <p><b>Escape closed everything.</b> Every open overlay had its own listener on
 * `document`, so one keypress ran all of them. Closing a drawer that had opened
 * a second drawer took both, and a confirm dialog raised from inside a drawer
 * dismissed the drawer underneath it — the reader lost the surface they were
 * standing on, having asked to dismiss the thing on top of it. This is not
 * hypothetical: the attention feed opens a due-date drawer inside itself, and
 * thirteen further surfaces pair a drawer with a Modal or a ConfirmModal.</p>
 *
 * <p><b>The scroll lock was released too early.</b> The inner overlay's cleanup
 * ran `body.overflow = ""` unconditionally, so dismissing it unlocked the page
 * while the outer one was still open and still modal.</p>
 *
 * <p>Both are fixed by the same idea: a module-level stack. Escape is handled by
 * the TOP entry only, and the scroll lock is released only when the stack
 * empties. The stack is module-level rather than context-based deliberately —
 * `Modal` portals to `document.body`, so a provider would have to wrap the app
 * to catch overlays that are not React-tree descendants of one another, and
 * these two behaviours are genuinely document-global.</p>
 *
 * <p><b>Not a focus trap.</b> Focus is `useFocusTrap`'s job and composes badly
 * when nested; this hook is only Escape and scroll, which compose fine because
 * exactly one owner is chosen.</p>
 *
 * <p>⚠️ <b>The ordering rule is "last to REGISTER", and registration happens in
 * effect order, which is child-first.</b> For every real stack in this app that
 * is the same thing as "last to open" — a drawer mounts, and some later
 * interaction raises a dialog inside it, so the dialog registers second and
 * owns Escape. It diverges only if two overlays mount in the SAME commit with
 * one nested inside the other: React runs the child's effect first, so the
 * INNER one registers first and the outer would take the key. That case is not
 * handled, deliberately. Resolving it properly means ranking by document order
 * or z-index at keypress time, which buys correctness in a situation this
 * codebase does not currently produce, at the cost of an element ref in the
 * hook's signature and a new failure mode when an overlay is not yet in the
 * DOM. If a surface ever does open a nested overlay in its first commit, that
 * is the moment to revisit — not before.</p>
 */

/** Open overlays, outermost first. The last entry owns Escape. */
const stack: symbol[] = [];

/** What `body.overflow` was before the first overlay locked it. */
let restoreOverflow: string | null = null;

export function useOverlayDismiss(active: boolean, onDismiss: () => void): void {
  // The callback rides a ref so it is NOT an effect dependency. If it were, a
  // caller passing an inline arrow — which is nearly all of them — would pop and
  // re-push its entry on every render, and the stack order would then reflect
  // render timing rather than open order. The overlay that owns Escape would
  // change without anything visibly happening.
  const cb = useRef(onDismiss);
  useEffect(() => {
    cb.current = onDismiss;
  });

  useEffect(() => {
    if (!active) return;

    const id = Symbol("overlay");
    if (stack.length === 0) restoreOverflow = document.body.style.overflow;
    stack.push(id);
    document.body.style.overflow = "hidden";

    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Top of the stack only. Every open overlay still hears the event; only
      // one acts on it.
      if (stack[stack.length - 1] !== id) return;
      cb.current();
    };
    document.addEventListener("keydown", handler);

    return () => {
      document.removeEventListener("keydown", handler);
      // Splice by identity, not pop: overlays do not always unmount in the
      // order they mounted (a parent route can drop an outer drawer while an
      // inner dialog is still open), and popping blindly would then remove
      // somebody else's entry and hand Escape to the wrong surface.
      const i = stack.indexOf(id);
      if (i !== -1) stack.splice(i, 1);
      if (stack.length === 0) {
        document.body.style.overflow = restoreOverflow ?? "";
        restoreOverflow = null;
      }
    };
  }, [active]);
}

/** Test-only: the stack is module state and leaks between cases otherwise. */
export function __resetOverlayDismissForTests(): void {
  stack.length = 0;
  restoreOverflow = null;
  document.body.style.overflow = "";
}
